import { ocrExecutionType } from '../../constants/enum';

/**
 * 比較の基準に使える実行ログの最小形。
 * Firestore の Timestamp をそのまま扱わず、ミリ秒に正規化して受け取る。
 *
 * Functions 側にも同じ実装がある（functions/src/utils/pattern/latestOcrExecution.ts）。
 * 相互 import ができないため両ツリーに置いている。片方だけ直すと
 * 提案（Functions）と精度表示（フロント）で基準が食い違うので、必ず両方に入れる。
 */
export type BaselineExecution = {
  execution_type: string;
  result_data: Record<string, unknown>;
  executed_at_ms: number;
};

/**
 * 「全項目実行」= 全項目を読み直している実行。
 * 項目のみ読み取り（re_ocr_single）は一部の項目しか上書きしないため、
 * 全項目の土台には使えない（#3318）。
 */
const isFullExecution = (execution: BaselineExecution): boolean =>
  execution.execution_type !== ocrExecutionType.reOcrSingle;

const byExecutedAtAsc = (a: BaselineExecution, b: BaselineExecution): number =>
  a.executed_at_ms - b.executed_at_ms;

/**
 * 比較の土台に使う「最新の全項目実行」を選ぶ。
 *
 * 再読み取りは results.result_data を上書きするが CREATE 履歴は更新しないため、
 * CREATE 履歴を基準にすると「人が直していない変更」まで差分として出てしまう（#3318）。
 *
 * 優先順位:
 *   1. 最新の全項目実行（initial / re_ocr_all / reclassification）
 *   2. initial（全項目実行が無い場合の保険）
 *   3. null（呼び出し側で CREATE 履歴にフォールバックする）
 */
export const selectBaselineExecution = (
  executions: BaselineExecution[] | null | undefined,
): BaselineExecution | null => {
  if (!executions || executions.length === 0) return null;

  const fullExecutions = executions.filter(isFullExecution);
  if (fullExecutions.length > 0) {
    // 取得順（executed_at 降順）に依存せず、ここで最新を選び直す
    return fullExecutions.reduce((latest, current) =>
      current.executed_at_ms > latest.executed_at_ms ? current : latest,
    );
  }

  return executions.find(e => e.execution_type === ocrExecutionType.initial) ?? null;
};

/**
 * 基準となる result_data を返す。該当する実行ログが無ければ null。
 *
 * 土台は「最新の全項目実行」だが、それ以降に項目のみ読み取り（re_ocr_single）が
 * あった項目は、その値が results.result_data に反映されている。
 * 土台のままだと人が直していないのに差分として出るため、
 * 項目のみ読み取りの結果を古い順に項目単位で重ねる（#3318）。
 *
 * 明細（配列）項目は、項目のみ読み取りが対象列だけを保存しているため、
 * 土台の行に対象列だけを上書きして行の他の列を保つ。
 */
export const selectBaselineResultData = (
  executions: BaselineExecution[] | null | undefined,
): Record<string, unknown> | null => {
  const baseline = selectBaselineExecution(executions);
  if (!baseline) return null;
  if (!executions || executions.length === 0) return baseline.result_data;

  // 土台より後に実行された項目のみ読み取りだけを、古い順に重ねる
  const singleExecutions = executions
    .filter(
      e =>
        e.execution_type === ocrExecutionType.reOcrSingle &&
        e.executed_at_ms > baseline.executed_at_ms,
    )
    .sort(byExecutedAtAsc);

  if (singleExecutions.length === 0) return baseline.result_data;

  const merged: Record<string, unknown> = { ...baseline.result_data };
  for (const execution of singleExecutions) {
    for (const [key, value] of Object.entries(execution.result_data ?? {})) {
      const baseRows = merged[key];
      // 明細項目は対象列だけが保存されているため、行ごとに列単位で重ねる
      if (Array.isArray(value) && Array.isArray(baseRows)) {
        merged[key] = baseRows.map((row, index) => {
          const patch = value[index];
          if (
            row === null ||
            typeof row !== 'object' ||
            patch === null ||
            typeof patch !== 'object'
          ) {
            return patch ?? row;
          }
          return { ...(row as Record<string, unknown>), ...(patch as Record<string, unknown>) };
        });
        continue;
      }
      merged[key] = value;
    }
  }
  return merged;
};
